import * as moment from 'moment';
import { Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { BackendService } from './backend.service';
import { UserService } from './user.service';
import { InvitedUser } from 'app/pages/team-select/team-select.component';
import { User } from '../models/User';

export interface Challenge {
  _id: string;
  name: string;
  preparation: Date;
  start: Date;
  // End date is last day of the challenge 23:59 in UTC+1, so this day is still within challenge time
  end: Date;
  minimalTeamMembers: number;
  maximumTeamMembers?: number;
  company: any;
  branchoffice: any;
  rewardsText?: string;
}

// A user within a team object
export interface TeamUser {
  membershipStatus: TeamMembershipStatus;
  isFounder: boolean;
  email: string;
  firstName?: string;
  lastName?: string;
  alias?: string;
  _id?: string;
  gender?: boolean;
  bonuses: {
    teatime: {
      days: number[];
    };
    diversity: {
      days: number[];
    };
    freshenUp: {
      days: number[];
    };
    salmagundi: {
      exerciseIDs: string[];
      completionDay?: number;
    };
  };
  points: {
    byDay: {
      [key: string]: number;
    };
    byWeek: {
      [key: string]: number;
    };
    total: number;
  };
}

// The team values, containing the users and additional user groups. The user groups
// are generated in frontend and used, to group team members for easier access.
export interface Team {
  _id: string;
  name: string;
  challengeID: string;
  users: TeamUser[];
  userGroups?: {
    invitations: TeamUser[];
    applications: TeamUser[];
    members: TeamUser[];
  };
  points: {
    byDay: {
      [key: string]: number;
    };
    byWeek: {
      [key: string]: number;
    };
    total: number;
  };
  // Array of email addresses from users who previously declined an invitation
  declinedInvitations: string[];
  teamLeavers: string[];
  bonuses: {
    weeklyWinner: {
      weeks: number[];
    };
  };
  hasInvitation?: boolean;
}

export enum TeamMembershipStatus {
  MEMBER = 'member',
  INVITATION = 'invitation',
  APPLICATION = 'application',
  NONE = 'none',
}

/**
 * Handles challenge related tasks.
 */
@Injectable()
export class ChallengeService {
  // The currently logged in user
  public user: User;

  // Flag which indicates that no challenges have been found, additionally to displayedChallenge subject
  public noChallengeFound = false;

  // The challenge that should be displayed in the app (Only one challenge should be displayed at any given time)
  // Use replay subjects in order for it to not emit on component initialization
  public displayedChallenge: Subject<Challenge> = new ReplaySubject(1);

  private currentChallengeId = null;

  // All teams for the currently displayed challenge
  public teams: Subject<Team[]> = new ReplaySubject(1);

  // Team of user for the currently displayed challenge
  public team: Subject<Team> = new ReplaySubject(1);

  // User object in team of currently displayed challenge
  public teamUser: Subject<TeamUser> = new ReplaySubject(1);

  constructor(private backendService: BackendService, private userService: UserService) {
    this.userService.currentUser.subscribe((user) => {
      if (!user) {
        return;
      }
      this.user = user;
      this.noChallengeFound = false;

      const query: any = {};

      if (user.company) {
        query.companyID = user.company._id;
      }

      if (user.branchoffice) {
        query.branchofficeID = user.branchoffice._id;
      }

      this.backendService.get<Challenge[]>(`challenge`, query).then((challenges) => {
        this.filterChallenges(challenges);
      });
    });
  }

  /**
   * Reloads teams for the currently active challenge
   */
  public async reloadTeams() {
    if (this.currentChallengeId) {
      try {
        const teams = await this.backendService.get<Team[]>(`challenge/${this.currentChallengeId}/team`);

        if (!teams || teams.length === 0) {
          this.teams.next([]);
          this.team.next(null);
          this.teamUser.next(null);
          return;
        }

        // Enhance team model by dividing users into different groups
        for (const team of teams) {
          const group = this.splitUserGroups(team);
          team.userGroups = group;
        }

        this.teams.next(teams);

        let newTeam = null;
        let newTeamUser = null;

        // Check if the user has member status in any team
        for (const team of teams) {
          for (const teamUser of team.userGroups.members) {
            if (this.user && teamUser._id === this.user._id) {
              newTeam = team;
              newTeamUser = teamUser;
            }
          }
        }

        if (!newTeam) {
          // Check if user has application status in any team
          for (const team of teams) {
            for (const teamUser of team.userGroups.applications) {
              if (this.user && teamUser._id === this.user._id) {
                newTeam = team;
                newTeamUser = teamUser;
              }
            }
          }
        }

        if (!newTeam) {
          // Check if user has invitation status in any team
          for (const team of teams) {
            for (const teamUser of team.userGroups.invitations) {
              if (this.user && teamUser._id === this.user._id) {
                newTeam = team;
                newTeamUser = teamUser;
              }
            }
          }
        }

        this.team.next(newTeam);
        this.teamUser.next(newTeamUser);
      } catch (error) {}
    }
  }

  /**
   * Removes a user from the specified team
   */
  public removeUserFromTeam(challengeID, teamID, userID): Promise<void> {
    return this.backendService.put(`challenge/${challengeID}/team/${teamID}/user/${userID}/leave`).then((result) => {
      this.reloadTeams();
    });
  }

  /**
   * Removes a user from the specified team
   */
  public declineInvitation(challengeID, teamID, invitedEmail): Promise<void> {
    return this.backendService.put(`challenge/${challengeID}/team/${teamID}/invitation/cancel`, { email: invitedEmail }).then((result) => {
      this.reloadTeams();
    });
  }

  /**
   * Checks whether a user is theoretically able to be invited to the team. But does not send invitation yet.
   */
  public checkUserEligibility(challengeID, teamID, email): Promise<InvitedUser> {
    return this.backendService.get(`challenge/${challengeID}/team/${teamID}/checkEligibility/${email}`);
  }

  /**
   * Invites a user to the specified team
   * @emails - list of emails of users that should be invited
   */
  public inviteUsers(challengeID, teamID, emails: string[]): Promise<void> {
    return this.backendService.put(`challenge/${challengeID}/team/${teamID}/invitation/create`, { emails }).then((result) => {
      this.reloadTeams();
    });
  }

  /**
   * Cheer for another team member in an ongoing challenge
   */
  public cheerForTeamMember(challengeID, teamID, userID): Promise<void> {
    return this.backendService.put(`challenge/${challengeID}/team/${teamID}/cheer/${userID}`).then((result) => {});
  }

  /**
   * Accept invitation to the specified team (triggered by invited user)
   */
  public acceptInvitation(challengeID, teamID, userID): Promise<void> {
    return this.backendService.put(`challenge/${challengeID}/team/${teamID}/invitation/accept`, { userID }).then((result) => {
      this.reloadTeams();
    });
  }

  /**
   * Accepts application of a user to the team (triggered by team founder)
   */
  public acceptApplication(challengeID, teamID, userID): Promise<void> {
    return this.backendService.put(`challenge/${challengeID}/team/${teamID}/application/accept`, { userID }).then((result) => {
      this.reloadTeams();
    });
  }

  /**
   * Cancels the application of a user for a team
   */
  public cancelApplication(challengeID, teamID, userID) {
    return this.backendService.put<any>(`challenge/${challengeID}/team/${teamID}/application/cancel`, { userID }).then((result) => {
      this.reloadTeams();
    });
  }

  /**
   * Send an application to join the specified team
   * @param challengeID Challenge the team takes part in
   * @param teamID Team user wishes to join
   */
  public applyForTeam(challengeID, teamID) {
    return this.backendService.put<any>(`challenge/${challengeID}/team/${teamID}/application/create`).then((result) => {
      this.reloadTeams();
    });
  }

  /**
   * Creates a team for the given challenge with the given values
   * @param teamValues - values of team that should be created
   */
  public createTeam(challengeID, teamValues): Promise<Team> {
    return this.backendService.post<any>(`challenge/${challengeID}/team`, teamValues).then(async (team) => {
      await this.reloadTeams();
      return team;
    });
  }

  /**
   * Deletes the specified team
   */
  public async deleteTeam(challengeID, teamID): Promise<void> {
    await this.backendService.delete_<any>(`challenge/${challengeID}/team/${teamID}`);
    await this.reloadTeams();
  }

  /**
   * Sets currentChallenge, nextChallenge and pastChallenges based on passed challenges.
   * Decides which challenge should be displayed throughout the app, since only one should be displayed at the same time.
   */
  private filterChallenges(challenges: Challenge[]) {
    if (!challenges) {
      this.noChallengeFound = true;
      return;
    }

    // Only past challenges that ended during the last 14 days are used
    const pastChallenges = challenges
      .filter((challenge) => moment().isAfter(moment(challenge.end)) && moment(challenge.end).add(7, 'days').isAfter(moment()))
      .sort((a, b) => (moment(a.end).isBefore(moment(b.end)) ? 1 : -1));

    const currentChallenges = challenges
      // Filter challenges that have started but not ended yet
      .filter((challenge) => moment().isAfter(moment(challenge.start)) && moment().isBefore(moment(challenge.end)))
      // Sort current challenges by start date (in case for some reason multiple challenges are active)
      .sort((a, b) => (moment(a.start).isBefore(moment(b.start)) ? 1 : -1));

    const futureChallenges = challenges
      // Filter challenges that have not started yet
      .filter(
        (challenge) => moment().isBefore(moment(challenge.start)) && (!challenge.preparation || moment().isAfter(challenge.preparation)),
      )
      // Sort future challenges by start date (in case for some reason multiple challenges are active)
      .sort((a, b) => (moment(a.start).isBefore(moment(b.start)) ? 1 : -1));

    // Decide which challenge should be displayed throughout the app
    let challengeToDisplay = null;

    if (currentChallenges && currentChallenges.length > 0) {
      challengeToDisplay = currentChallenges[0];
    }

    if (!challengeToDisplay && futureChallenges && futureChallenges.length > 0) {
      challengeToDisplay = futureChallenges[0];
    }

    if (!challengeToDisplay && pastChallenges && pastChallenges.length > 0) {
      challengeToDisplay = pastChallenges[0];
    }

    if (challengeToDisplay) {
      this.displayedChallenge.next(challengeToDisplay);
      this.currentChallengeId = challengeToDisplay._id;
      this.reloadTeams();
    } else {
      this.noChallengeFound = true;
      this.displayedChallenge.next(null);
      this.currentChallengeId = null;
      this.teams.next(null);
      this.team.next(null);
      this.teamUser.next(null);
    }
  }

  /**
   * Splits the users of the team into different membership groups
   */
  private splitUserGroups(team: Team) {
    if (!team) {
      return;
    }

    const userGroups = {
      invitations: [],
      applications: [],
      members: [],
    };

    // Divide users into different arrays based on their membership status
    for (const user of team.users) {
      if (user.membershipStatus === TeamMembershipStatus.MEMBER) {
        userGroups.members.push(user);
      }
      if (user.membershipStatus === TeamMembershipStatus.INVITATION) {
        userGroups.invitations.push(user);
      }
      if (user.membershipStatus === TeamMembershipStatus.APPLICATION) {
        userGroups.applications.push(user);
      }
    }
    return userGroups;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src1521087412/src/client/web-client/src/app/shared/services/challenge.service.ts